import { useState, useRef, useEffect } from 'react';
import '../DatePicker/DatePickerStyle.scss';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const DatePicker = ({ value, onChange, placeholder = 'Select date' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);
  const datePickerRef = useRef(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsYearPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  const handleDateClick = (day) => {
    const newDate = new Date(currentYear, currentMonth, day);
    setSelectedDate(newDate);
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleYearSelect = (year) => {
    setCurrentYear(year);
    setIsYearPickerOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setIsYearPickerOpen(false);
    setSelectedDate(value || new Date());
    setCurrentMonth(new Date().getMonth());
    setCurrentYear(new Date().getFullYear());
  };

  const handleOk = () => {
    if (onChange) {
      onChange(selectedDate);
    }
    setIsOpen(false);
    setIsYearPickerOpen(false);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    const today = new Date();
    const isCurrentMonth = currentMonth === today.getMonth() && currentYear === today.getFullYear();

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = isCurrentMonth && day === today.getDate();
      const isSelected =
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentMonth &&
        selectedDate.getFullYear() === currentYear;

      const isPast = new Date(currentYear, currentMonth, day) <
        new Date(today.getFullYear(), today.getMonth(), today.getDate());

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isPast ? 'disabled' : ''}`}
          onClick={() => !isPast && handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const renderYearPicker = () => {
    const years = [];
    const startYear = new Date().getFullYear();
    const endYear = 2100;

    for (let year = startYear; year <= endYear; year++) {
      years.push(
        <div
          key={year}
          className={`year-option ${year === currentYear ? 'selected' : ''}`}
          onClick={() => handleYearSelect(year)}
        >
          {year}
        </div>
      );
    }

    return years;
  };

  return (
    <div className="date-picker-container" ref={datePickerRef}>
      <input
        type="text"
        className="date-picker-input"
        value={value ? formatDate(value) : ''}
        placeholder={placeholder}
        readOnly
        onClick={() => setIsOpen(!isOpen)}
      />

      {isOpen && (
        <div className="date-picker-dropdown">
          <div className="date-picker-header">
            <label className="year-label">Select year</label>
            <input
              type="text"
              className="year-input"
              value={currentYear}
              readOnly
              onClick={() => setIsYearPickerOpen(!isYearPickerOpen)}
            />

            {isYearPickerOpen && (
              <div className="year-picker-dropdown">
                {renderYearPicker()}
              </div>
            )}

            <div className="selected-date">
              {formatDate(selectedDate)}
            </div>
          </div>

          <div className="calendar-header">
            <button className="nav-button" onClick={handlePrevMonth}>
              <ChevronLeft size={20} />
            </button>
            <div className="month-year">
              {monthNames[currentMonth]} {currentYear}
            </div>
            <button className="nav-button" onClick={handleNextMonth}>
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="calendar-grid">
            {dayNames.map(day => (
              <div key={day} className="day-name">
                {day}
              </div>
            ))}
            {renderCalendar()}
          </div>

          <div className="date-picker-footer">
            <button className="cancel-button" onClick={handleCancel}>
              CANCEL
            </button>
            <button className="ok-button" onClick={handleOk}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
